import type { ClientListRequest } from '@ghp/api-client'
import { z } from 'zod'

import { urlCount, urlText } from '../../../lib/search-params'

/**
 * Le modèle des filtres de l'écran « Clients & lots » : le schéma des `search params`, les vues,
 * et la **couture** vers la requête serveur. Hors de la route pour que le composant d'écran reste
 * du markup, et hors du module d'écran pour que `validateSearch` puisse le charger sans ramener
 * l'écran dans le bundle d'entrée (`lazyRouteComponent`).
 *
 * ⚠️ **Aucun filtrage en mémoire** (D24) : page, recherche, tri et compteurs sont calculés en
 * base. Ce qui vit ici est le schéma de l'URL — l'URL est l'état, un filtre se partage en copiant
 * l'adresse — et sa traduction en paramètres de requête.
 *
 * Chaque champ porte un `.catch` : un paramètre inattendu retombe sur son défaut au lieu de casser
 * l'écran (M21 appliqué à l'URL).
 */

/**
 * Une vue, en un seul endroit : son libellé, et **ce que le serveur en fait**. Les cinq sont
 * celles du bandeau segmenté de la maquette, dans son ordre.
 *
 * ⚠️ **Une vue n'est pas un concept, c'est un préréglage de filtres** (M14 § 1) — et les cinq
 * d'ici ne prérèglent pas toutes la même chose : les quatre premières posent une **famille**, « Par
 * ville » pose un **tri**. C'est pourquoi elle n'a pas de compteur : elle ne retire personne du
 * répertoire, elle le range autrement, et un nombre à côté ferait croire à un sous-ensemble.
 */
interface ViewDefinition {
  value: string
  label: string
  family: ClientListRequest['family']
  sort: ClientListRequest['sort']
  /** `false` quand la vue n'est pas un sous-ensemble : le segment s'affiche alors sans nombre. */
  counted: boolean
  /** Le répertoire archivé plutôt que l'actif — la seule vue qui le pose. */
  archived?: ClientListRequest['archived']
}

export const VIEWS = [
  { value: 'all', label: 'Tous', family: 'all', sort: 'name', counted: true },
  {
    value: 'municipality',
    label: 'Communes',
    family: 'municipality',
    sort: 'name',
    counted: true,
  },
  {
    value: 'private_professional',
    label: 'Promoteurs, syndics & bailleurs',
    family: 'private_professional',
    sort: 'name',
    counted: true,
  },
  {
    value: 'individual',
    label: 'Particuliers',
    family: 'individual',
    sort: 'name',
    counted: true,
  },
  { value: 'city', label: 'Par ville', family: 'all', sort: 'city', counted: false },
  /*
   * ⚠️ **Un ajout à la maquette**, qui n'en a pas : archiver un particulier le retirait du
   * répertoire sans aucun chemin de retour depuis l'écran. Pas de compteur — les nombres des
   * autres segments se comptent sur l'actif.
   */
  {
    value: 'archived',
    label: 'Archivés',
    family: 'all',
    sort: 'name',
    counted: false,
    archived: 'true',
  },
] as const satisfies readonly ViewDefinition[]

/** La vue est **dérivée** de la table — jamais une seconde liste de littéraux à tenir. */
export type ClientListView = (typeof VIEWS)[number]['value']

const VIEW_VALUES = VIEWS.map((view) => view.value) as [ClientListView, ...ClientListView[]]

export const clientListSearchSchema = z.object({
  view: z.enum(VIEW_VALUES).default('all').catch('all'),
  q: urlText(''),
  offset: urlCount(0),
})

export type ClientListSearch = z.infer<typeof clientListSearchSchema>

/** Les valeurs par défaut du schéma, partagées entre l'URL et « Réinitialiser ». */
export const CLIENT_LIST_DEFAULTS: ClientListSearch = clientListSearchSchema.parse({})

/**
 * Combien de lignes une page demande. Le répertoire est un annuaire qu'on parcourt : cinquante
 * lignes tiennent dans une zone défilante sans que la pagination devienne le geste principal.
 */
export const CLIENT_PAGE_SIZE = 50

/**
 * **La couture** : l'état d'URL de l'écran vers ce que l'API accepte (D21). C'est ici — et nulle
 * part ailleurs — que la vue devient une famille et un tri.
 */
export function toClientListQuery(search: ClientListSearch): ClientListRequest {
  const view = VIEWS.find(({ value }) => value === search.view)

  return {
    search: search.q.trim(),
    family: view?.family ?? 'all',
    ...(view !== undefined && 'archived' in view ? { archived: view.archived } : {}),
    sort: view?.sort ?? 'name',
    limit: CLIENT_PAGE_SIZE,
    offset: search.offset,
  }
}
